'use client'

import React, {useCallback, useId, useRef} from 'react'
import {Search, X} from 'lucide-react'
import {Input} from '@fitro/customer-ui/Input'
import {cn} from '../../../lib/utils'
import {useTranslation} from '../../../hooks/useTranslation'

interface SearchInputProps {
    value?: string
    onChange: (value: string) => void
    onSubmit?: (value: string) => void
    placeholder?: string
    disabled?: boolean
    className?: string
    inputClassName?: string
    autoFocus?: boolean
    size?: 'small' | 'medium' | 'large'
}

export const SearchInput: React.FC<SearchInputProps> = ({
    value = '',
    onChange,
    onSubmit,
    placeholder,
    disabled = false,
    className,
    inputClassName,
    autoFocus = false,
    size = 'medium'
}) => {
    const {t} = useTranslation('common')
    const inputId = useId()
    const inputRef = useRef<HTMLInputElement>(null)
    const resolvedPlaceholder = placeholder || t('actions.search')

    const handleSubmit = useCallback((e: React.FormEvent) => {
        e.preventDefault()

        if (value.trim()) {
            onSubmit?.(value.trim())
            onChange('')
        }
    }, [value, onSubmit, onChange])

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            if (value.trim()) {
                onSubmit?.(value.trim())
                onChange('')
            }
        }
    }, [value, onSubmit, onChange])

    const handleClear = useCallback(() => {
        onChange('')
        inputRef.current?.focus()
    }, [onChange])

    const shouldShowClearButton = value.length > 0 && !disabled

    return (
        <form onSubmit={handleSubmit} className={cn('relative', className)}>
            <label htmlFor={inputId} className="sr-only">{resolvedPlaceholder}</label>
            <Input
                ref={inputRef}
                id={inputId}
                type="text"
                enterKeyHint="search"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={resolvedPlaceholder}
                disabled={disabled}
                autoFocus={autoFocus}
                size={size}
                leftIcon={
                    <div className="text-gray-400 dark:text-gray-500">
                        <Search/>
                    </div>
                }
                rightIcon={
                    shouldShowClearButton ? (
                        <div className="flex items-center justify-center">
                            <button
                                type="button"
                                onClick={handleClear}
                                className={cn(
                                    'hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full',
                                    'transition-colors focus-visible:ring-2 focus-visible:ring-primary-500',
                                    'focus-visible:ring-offset-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300',
                                    'flex items-center justify-center p-1'
                                )}
                                aria-label={t('search.clear')}
                            >
                                <X/>
                            </button>
                        </div>
                    ) : undefined
                }
                rightIconInteractive={shouldShowClearButton}
                className={cn('w-full', inputClassName)}
            />
        </form>
    )
}

SearchInput.displayName = 'SearchInput'
